import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Grid, 
  Button, 
  Divider,
  IconButton,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

const PredictionPage = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('month');
  const [predictionMethod, setPredictionMethod] = useState('sma');
  const [startDate, setStartDate] = useState(new Date(2025, 4, 1)); // 2025년 5월 1일
  const [endDate, setEndDate] = useState(new Date(2025, 7, 31)); // 2025년 8월 31일
  const [activityType, setActivityType] = useState('all');
  
  // 예측 데이터 (실제 앱에서는 API에서 가져올 것)
  const predictionData = [
    { date: '2025-05-01', actual: 12.5, predicted: null, lower: null, upper: null },
    { date: '2025-05-08', actual: 13.2, predicted: null, lower: null, upper: null },
    { date: '2025-05-15', actual: 11.8, predicted: null, lower: null, upper: null },
    { date: '2025-05-22', actual: 12.3, predicted: null, lower: null, upper: null },
    { date: '2025-05-29', actual: 13.5, predicted: null, lower: null, upper: null },
    { date: '2025-06-05', actual: 14.1, predicted: null, lower: null, upper: null },
    { date: '2025-06-12', actual: 13.7, predicted: null, lower: null, upper: null },
    { date: '2025-06-19', actual: 12.9, predicted: null, lower: null, upper: null },
    { date: '2025-06-26', actual: 12.2, predicted: null, lower: null, upper: null },
    { date: '2025-07-03', actual: 11.5, predicted: 11.8, lower: 10.5, upper: 13.1 },
    { date: '2025-07-10', actual: 11.9, predicted: 11.6, lower: 10.2, upper: 13.0 },
    { date: '2025-07-17', actual: 12.7, predicted: 12.2, lower: 10.8, upper: 13.6 },
    { date: '2025-07-24', actual: 13.3, predicted: 12.8, lower: 11.4, upper: 14.2 },
    { date: '2025-07-31', actual: 13.8, predicted: 13.5, lower: 12.1, upper: 14.9 },
    { date: '2025-08-07', actual: null, predicted: 13.9, lower: 12.5, upper: 15.3 },
    { date: '2025-08-14', actual: null, predicted: 14.2, lower: 12.8, upper: 15.6 },
    { date: '2025-08-21', actual: null, predicted: 14.5, lower: 13.1, upper: 15.9 },
    { date: '2025-08-28', actual: null, predicted: 14.8, lower: 13.4, upper: 16.2 },
  ];
  
  // 예측 방법 설명
  const predictionMethodDescriptions = {
    sma: '단순 이동 평균(SMA)은 과거 데이터의 평균을 사용하여 미래 값을 예측합니다. 단기 예측에 적합합니다.',
    ema: '지수 이동 평균(EMA)은 최근 데이터에 더 높은 가중치를 부여하여 예측합니다. 추세 변화에 더 민감합니다.',
    arima: 'ARIMA(자기회귀 통합 이동 평균)는 시계열 데이터의 패턴을 분석하여 예측합니다. 복잡한 패턴에 적합합니다.',
    ml: '머신러닝 모델은 다양한 변수와 패턴을 학습하여 예측합니다. 대량의 데이터와 복잡한 관계에 적합합니다.'
  };
  
  // 시간 범위 변경 핸들러
  const handleTimeRangeChange = (event, newTimeRange) => {
    if (newTimeRange !== null) {
      setTimeRange(newTimeRange);
    }
  };
  
  // 예측 방법 변경 핸들러
  const handlePredictionMethodChange = (event) => {
    setPredictionMethod(event.target.value);
  };
  
  // 활동 유형 변경 핸들러
  const handleActivityTypeChange = (event) => {
    setActivityType(event.target.value);
  };
  
  // 예측 실행 핸들러
  const handleRunPrediction = () => {
    console.log('예측 실행:', {
      timeRange,
      predictionMethod,
      startDate,
      endDate,
      activityType
    });
    // 실제 앱에서는 여기서 API 호출을 통해 예측 데이터를 가져옴
  };
  
  return (
    <Box sx={{ flexGrow: 1, py: 0 }}>
      {/* <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        탄소 배출량 예측
      </Typography> */}
      
      <Grid container spacing={3}>
        {/* 예측 설정 카드 */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', minHeight: 160 }}>
            <CardHeader 
              title="예측 설정" 
              subheader="예측 매개변수를 설정하세요"
            />
            <Divider />
            <CardContent>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="prediction-method-label">예측 방법</InputLabel>
                      <Select
                        labelId="prediction-method-label"
                        id="prediction-method"
                        value={predictionMethod}
                        label="예측 방법"
                        onChange={handlePredictionMethodChange}
                      >
                        <MenuItem value="sma">단순 이동 평균 (SMA)</MenuItem>
                        <MenuItem value="ema">지수 이동 평균 (EMA)</MenuItem>
                        <MenuItem value="arima">ARIMA 모델</MenuItem>
                        <MenuItem value="ml">머신러닝 모델</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                      {predictionMethodDescriptions[predictionMethod]}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="activity-type-label">활동 유형</InputLabel>
                      <Select
                        labelId="activity-type-label"
                        id="activity-type"
                        value={activityType}
                        label="활동 유형"
                        onChange={handleActivityTypeChange}
                      >
                        <MenuItem value="all">모든 활동</MenuItem>
                        <MenuItem value="transportation">교통</MenuItem>
                        <MenuItem value="electricity">전기</MenuItem>
                        <MenuItem value="heating">난방</MenuItem>
                        <MenuItem value="food">식품</MenuItem>
                        <MenuItem value="other">기타</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="시작 날짜"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: '예측 기간 시작'
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="종료 날짜"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: '예측 기간 종료'
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      시간 단위
                    </Typography>
                    <ToggleButtonGroup
                      value={timeRange}
                      exclusive
                      onChange={handleTimeRangeChange}
                      aria-label="시간 범위"
                      fullWidth
                    >
                      <ToggleButton value="week" aria-label="주간">
                        <CalendarViewWeekIcon sx={{ mr: 1 }} />
                        주간
                      </ToggleButton>
                      <ToggleButton value="month" aria-label="월간">
                        <CalendarViewMonthIcon sx={{ mr: 1 }} />
                        월간
                      </ToggleButton>
                      <ToggleButton value="quarter" aria-label="분기">
                        <CalendarViewDayIcon sx={{ mr: 1 }} />
                        분기
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Button 
                      variant="contained" 
                      fullWidth
                      onClick={handleRunPrediction}
                      sx={{ mt: 2 }}
                    >
                      예측 실행
                    </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 예측 차트 카드 */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="탄소 배출량 예측 차트" 
              subheader="실제 데이터와 예측 데이터 비교"
              action={
                <IconButton aria-label="settings">
                  <MoreVertIcon />
                </IconButton>
              }
            />
            <Divider />
            <CardContent>
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={predictionData}
                    margin={{ top: 20, right: 10, left: 10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.background.lighter} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: theme.palette.text.secondary }}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis 
                      tick={{ fill: theme.palette.text.secondary }}
                      axisLine={{ stroke: theme.palette.divider }}
                      unit=" tCO₂e"
                    />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="upper" 
                      stroke="transparent"
                      fill={theme.palette.primary.main}
                      fillOpacity={0.1}
                      name="예측 상한"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="lower" 
                      stroke="transparent"
                      fill={theme.palette.primary.main}
                      fillOpacity={0.1}
                      name="예측 하한"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="actual" 
                      stroke={theme.palette.chart.c2} 
                      strokeWidth={2}
                      dot={{ r: 4, fill: theme.palette.chart.c2 }}
                      activeDot={{ r: 6 }}
                      name="실제 배출량"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="predicted" 
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 4, fill: theme.palette.primary.main }}
                      activeDot={{ r: 6 }}
                      name="예측 배출량"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                  예측 요약
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          예상 평균 배출량
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
                          14.35 tCO₂e
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          예상 최대 배출량
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold', color: theme.palette.error.main }}>
                          16.20 tCO₂e
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          예상 최소 배출량
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold', color: theme.palette.success.main }}>
                          12.50 tCO₂e
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 예측 정확도 카드 */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="예측 정확도 분석" 
              subheader="이전 예측의 정확도 평가"
              action={
                <Tooltip title="예측 정확도는 과거 예측과 실제 값의 비교를 통해 계산됩니다">
                  <IconButton>
                    <InfoOutlinedIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={[
                          { month: '1월', sma: 85, ema: 82, arima: 88, ml: 90 },
                          { month: '2월', sma: 83, ema: 84, arima: 87, ml: 91 },
                          { month: '3월', sma: 86, ema: 85, arima: 89, ml: 92 },
                          { month: '4월', sma: 84, ema: 83, arima: 90, ml: 93 },
                          { month: '5월', sma: 87, ema: 86, arima: 91, ml: 94 },
                        ]}
                        margin={{ top: 15, right: 20, left: 10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.background.lighter} />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fill: theme.palette.text.secondary }}
                          axisLine={{ stroke: theme.palette.divider }}
                        />
                        <YAxis 
                          tick={{ fill: theme.palette.text.secondary }}
                          axisLine={{ stroke: theme.palette.divider }}
                          unit="%"
                        />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="sma" 
                          name="SMA" 
                          stroke={theme.palette.primary.main} 
                          activeDot={{ r: 8 }} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="ema" 
                          name="EMA" 
                          stroke={theme.palette.chart.c2} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="arima" 
                          name="ARIMA" 
                          stroke={theme.palette.chart.c3} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="ml" 
                          name="ML" 
                          stroke={theme.palette.chart.c4} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    예측 모델 성능 비교
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        단순 이동 평균 (SMA)
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Box
                          sx={{
                            width: '100%',
                            mr: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'background.lighter',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: '85%',
                              bgcolor: theme.palette.primary.main,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          85%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        지수 이동 평균 (EMA)
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Box
                          sx={{
                            width: '100%',
                            mr: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'background.lighter',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: '84%',
                              bgcolor: theme.palette.chart.c2,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          84%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        ARIMA 모델
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Box
                          sx={{
                            width: '100%',
                            mr: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'background.lighter',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: '89%',
                              bgcolor: theme.palette.chart.c3,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          89%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        머신러닝 모델
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Box
                          sx={{
                            width: '100%',
                            mr: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'background.lighter',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: '92%',
                              bgcolor: theme.palette.chart.c4,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          92%
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
                    예측 오차 분석
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Typography variant="body2" color="text.secondary">
                            평균 절대 오차 (MAE)
                          </Typography>
                          <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
                            1.24 tCO₂e
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{ backgroundColor: 'background.lighter' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Typography variant="body2" color="text.secondary">
                            평균 제곱근 오차 (RMSE)
                          </Typography>
                          <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
                            1.68 tCO₂e
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PredictionPage;
